import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Edit3, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { backendUrl } from "@/lib/api";
import LocationFields from "@/components/LocationFields";
import HighlightText from "@/components/data-management/HighlightText";
import SavedViewsBar from "@/components/data-management/SavedViewsBar";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";
import { normalizeListResponse } from "@/lib/listResponse";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";
import { CONTACTS } from "@/constants/testIds/crm";

const inputCls = "w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors";
const SORT_OPTIONS = [
    { value: "-created_at", label: "Newest first" },
    { value: "created_at", label: "Oldest first" },
    { value: "company", label: "Company A-Z" },
    { value: "-company", label: "Company Z-A" },
    { value: "client_name", label: "Client A-Z" },
    { value: "source", label: "Source A-Z" },
];

function EmptyForm() {
    return {
        company: "",
        company_email: "",
        client_name: "",
        client_email: "",
        phone: "",
        website: "",
        country: "",
        state: "",
        city: "",
        source: "",
    };
}

export default function Contacts() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [source, setSource] = useState("");
    const [country, setCountry] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");
    const [sort, setSort] = useState("-created_at");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EmptyForm());
    const [modalOpen, setModalOpen] = useState(false);

    const load = useCallback(async () => {
        const params = new URLSearchParams();
        params.set("page", page);
        params.set("limit", 12);
        if (q) params.set("q", q);
        if (source) params.set("source", source);
        if (country) params.set("country", country);
        if (state) params.set("state", state);
        if (city) params.set("city", city);
        if (sort) params.set("sort", sort);
        const { data } = await api.get(`/contacts?${params.toString()}`);
        const normalized = normalizeListResponse(data);
        setItems(normalized.items);
        setCounts(normalized.counts);
        setTotal(normalized.total);
    }, [city, country, page, q, source, sort, state]);
    useEffect(() => { load().catch((err) => toast.error(err.response?.data?.detail || err.message || "Failed to load contacts")); }, [load]);

    const openCreate = () => { setEditing(null); setForm(EmptyForm()); setModalOpen(true); };
    const openEdit = (item) => { setEditing(item); setForm(item); setModalOpen(true); };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing?.id) await api.patch(`/contacts/${editing.id}`, form);
            else await api.post("/contacts", form);
            toast.success(editing?.id ? "Contact updated" : "Contact created");
            setEditing(null);
            setForm(EmptyForm());
            setModalOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Save failed");
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Delete contact "${item.company}"?`)) return;
        await api.delete(`/contacts/${item.id}`);
        toast.success("Contact deleted");
        load();
    };

    const exportCsv = () => {
        window.open(backendUrl("/api/contacts/export"), "_blank", "noopener,noreferrer");
    };

    const sources = useMemo(() => Array.from(new Set(items.map((i) => i.source).filter(Boolean))), [items]);
    const viewState = { q, source, country, state, city, sort };
    const applyState = (next) => {
        setQ(next.q || "");
        setSource(next.source || "");
        setCountry(next.country || "");
        setState(next.state || "");
        setCity(next.city || "");
        setSort(next.sort || "-created_at");
        setPage(1);
    };
    const sourceCards = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([label, value]) => ({ label, value, hint: "Contacts by source" }));

    return (
        <div className="space-y-6" data-testid={CONTACTS.root}>
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">CRM</div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Contacts</h1>
                    <p className="text-sm text-muted-foreground mt-2">A deduplicated contact book mirrored from leads.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={exportCsv} className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-border bg-card hover:bg-secondary text-sm">
                        <Download className="h-4 w-4" /> Export CSV
                    </button>
                    <button onClick={openCreate} data-testid={CONTACTS.createButton} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                        <Plus className="h-4 w-4" /> New Contact
                    </button>
                </div>
            </div>

            <SavedViewsBar
                storageKey="crm.contacts.views"
                state={viewState}
                applyState={applyState}
                defaultState={{ q: "", source: "", country: "", state: "", city: "", sort: "-created_at" }}
            />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search company, client, email, phone…" className="md:col-span-2 h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={source} onChange={(e) => { setPage(1); setSource(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All sources</option>
                    {sources.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input value={country} onChange={(e) => { setPage(1); setCountry(e.target.value); }} placeholder="Country" className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input value={state} onChange={(e) => { setPage(1); setState(e.target.value); }} placeholder="State" className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input value={city} onChange={(e) => { setPage(1); setCity(e.target.value); }} placeholder="City" className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            </div>

            <StatusCountCards
                cards={[
                    { label: "Total Contacts", value: total, hint: "All matching records" },
                    ...sourceCards,
                ]}
                className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3"
            />

            <div className="border border-border rounded-md bg-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 border-b border-border">
                        <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                            <th className="px-4 py-3">Company</th>
                            <th className="px-4 py-3">Company Email</th>
                            <th className="px-4 py-3">Client</th>
                            <th className="px-4 py-3">Client Email</th>
                            <th className="px-4 py-3">Phone</th>
                            <th className="px-4 py-3">Source</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No contacts.</td></tr>}
                        {items.map((c) => (
                            <tr key={c.id} data-testid={CONTACTS.row} className={`border-b border-border last:border-0 hover:brightness-[0.99] transition-colors ${getStatusToneClasses(c.source, { group: "source", variant: "row" })}`}>
                                <td className="px-4 py-3 font-medium"><HighlightText text={c.company} query={q} /></td>
                                <td className="px-4 py-3 text-muted-foreground text-xs font-mono"><HighlightText text={c.company_email || "—"} query={q} /></td>
                                <td className="px-4 py-3"><HighlightText text={c.client_name} query={q} /></td>
                                <td className="px-4 py-3 text-muted-foreground text-xs font-mono"><HighlightText text={c.client_email || "—"} query={q} /></td>
                                <td className="px-4 py-3 text-muted-foreground text-xs font-mono"><HighlightText text={c.phone || "—"} query={q} /></td>
                                <td className="px-4 py-3">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${getStatusToneClasses(c.source, { group: "source", variant: "badge" })}`}>
                                        {getStatusLabel(c.source)}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-1">
                                        <button onClick={() => openEdit(c)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary"><Edit3 className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => remove(c)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-destructive/10 text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <PaginationBar total={total} page={page} limit={12} onPageChange={setPage} />
            </div>

            {modalOpen && (
                <ContactModal
                    form={form}
                    setForm={setForm}
                    onClose={() => { setEditing(null); setForm(EmptyForm()); setModalOpen(false); }}
                    onSave={save}
                    title={editing?.id ? "Edit Contact" : "Create Contact"}
                />
            )}
        </div>
    );
}

function ContactModal({ form, setForm, onClose, onSave, title }) {
    return (
        <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={onSave} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-card border border-border rounded-md shadow-xl">
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">{title}</div>
                        <h3 className="font-heading text-xl font-semibold mt-1">{title}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        ["Company", "company"],
                        ["Company Email", "company_email"],
                        ["Client Name", "client_name"],
                        ["Client Email", "client_email"],
                        ["Phone", "phone"],
                        ["Website", "website"],
                    ].map(([label, key]) => (
                        <label key={key} className="space-y-1.5">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
                            <input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputCls} />
                        </label>
                    ))}
                    <LocationFields form={form} setForm={setForm} />
                    <label className="space-y-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Source</span>
                        <input value={form.source || ""} onChange={(e) => setForm({ ...form, source: e.target.value })} className={inputCls} />
                    </label>
                </div>
                <div className="p-4 border-t border-border flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm">Cancel</button>
                    <button className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">Save</button>
                </div>
            </form>
        </div>
    );
}
